import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';

const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  // 2FA state
  const [pendingToken, setPendingToken] = useState(null);
  const [twoFactorCode, setTwoFactorCode] = useState('');

  const { login, loginVerify2FA } = useAuth();
  const navigate = useNavigate();

  const routeForRole = (role) => {
    if (role === 'admin') return '/admin/dashboard';
    if (role === 'barber') return '/barber/dashboard';
    return '/';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const result = await login(email, password);

      if (result.success) {
        toast.success('Welcome back!');
        navigate(routeForRole(result.user.role));
      } else if (result.requires2fa) {
        setPendingToken(result.pendingToken);
        toast.info(result.error || 'Enter your 6-digit authenticator code');
      } else {
        toast.error(result.error);
      }
    } catch (error) {
      console.error('Login error:', error);
      toast.error('Login failed. Please check your connection and try again.');
    } finally {
      setLoading(false);
    }
  };

  const handle2FASubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const result = await loginVerify2FA(pendingToken, twoFactorCode);
      if (result.success) {
        toast.success('Welcome back!');
        navigate(routeForRole(result.user.role));
      } else {
        toast.error(result.error);
      }
    } catch (error) {
      console.error('2FA error:', error);
      toast.error('Verification failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const inputStyle = {
    width: '100%',
    padding: '14px 16px',
    background: 'rgba(10,10,10,0.88)',
    border: '1px solid rgba(170,255,0,0.18)',
    borderRadius: 'var(--ss-radius-md)',
    color: 'var(--ss-white)',
    fontSize: '1rem',
    outline: 'none',
    transition: 'border-color 0.2s ease',
  };

  return (
    <div className="sauce-sity-page" data-testid="login-page" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '24px' }}>
      <div className="sauce-sity-content" style={{ maxWidth: 480, width: '100%' }}>
        <div className="ss-card ss-card-purple" data-testid="login-card">
          <div className="ss-text-center ss-mb-24">
            <img
              src="https://customer-assets.emergentagent.com/job_barber-cape-coral/artifacts/b9xk2g3p_761.png"
              alt="Sauce Sity Logo"
              style={{ height: 112, width: 112, objectFit: 'contain', margin: '0 auto 12px' }}
            />
            {pendingToken ? (
              <>
                <h1 className="ss-title-lg">Two-Factor <span className="ss-highlight-green">Code</span></h1>
                <p className="ss-text">Open your authenticator app and enter the 6-digit code</p>
              </>
            ) : (
              <>
                <h1 className="ss-title-lg">Welcome <span className="ss-highlight-green">Back</span></h1>
                <p className="ss-text">Sign in to your Sauce Sity account</p>
              </>
            )}
          </div>

          {pendingToken ? (
            <form onSubmit={handle2FASubmit} style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              <div>
                <label htmlFor="twofa-code" style={{ display: 'block', color: 'var(--ss-white)', fontWeight: 600, marginBottom: 6 }}>
                  6-digit code (or recovery code)
                </label>
                <input
                  id="twofa-code"
                  data-testid="login-2fa-input"
                  type="text"
                  placeholder="123456"
                  value={twoFactorCode}
                  onChange={(e) => setTwoFactorCode(e.target.value)}
                  required
                  autoFocus
                  style={{ ...inputStyle, letterSpacing: '0.3em', textAlign: 'center', fontSize: '1.2rem' }}
                />
              </div>
              <button
                type="submit"
                data-testid="login-2fa-submit-btn"
                disabled={loading}
                className="ss-btn ss-btn-primary ss-btn-full"
                style={{ opacity: loading ? 0.6 : 1, cursor: loading ? 'not-allowed' : 'pointer' }}
              >
                {loading ? 'Verifying…' : 'Verify'}
              </button>
              <button
                type="button"
                onClick={() => { setPendingToken(null); setTwoFactorCode(''); }}
                className="ss-btn ss-btn-secondary ss-btn-full"
                data-testid="login-2fa-cancel-btn"
              >
                Cancel
              </button>
            </form>
          ) : (
            <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              <div>
                <label htmlFor="email" style={{ display: 'block', color: 'var(--ss-white)', fontWeight: 600, marginBottom: 6 }}>Email</label>
                <input
                  id="email"
                  data-testid="login-email-input"
                  type="email"
                  placeholder="your@email.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  style={inputStyle}
                />
              </div>
              <div>
                <label htmlFor="password" style={{ display: 'block', color: 'var(--ss-white)', fontWeight: 600, marginBottom: 6 }}>Password</label>
                <input
                  id="password"
                  data-testid="login-password-input"
                  type="password"
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  style={inputStyle}
                />
              </div>
              <button
                type="submit"
                data-testid="login-submit-btn"
                disabled={loading}
                className="ss-btn ss-btn-primary ss-btn-full"
                style={{ opacity: loading ? 0.6 : 1, cursor: loading ? 'not-allowed' : 'pointer' }}
              >
                {loading ? 'Signing in…' : 'Sign In'}
              </button>
            </form>
          )}

          {!pendingToken && (
            <>
              <div className="ss-text-center ss-mt-24">
                <Link to="/forgot-password" data-testid="login-forgot-link" style={{ color: 'var(--ss-green)', fontWeight: 600, textDecoration: 'none' }}>
                  Forgot password?
                </Link>
              </div>

              <div className="ss-text-center ss-mt-24">
                <p className="ss-text">
                  Don't have an account?{' '}
                  <Link to="/signup" data-testid="login-signup-link" style={{ color: 'var(--ss-green)', fontWeight: 700, textDecoration: 'none' }}>
                    Sign up
                  </Link>
                </p>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default Login;
